import type { Schema } from "./api";
import { registerPrice } from "./resourcePrice.ts";

type ResourceLine = Schema["ResourceLine"];

export type ResourceAction = {
  key: "quantity" | "unit" | "price" | "equipment";
  topic: string;
  title: string;
  action: string;
  tone: "missing" | "review" | "price";
  evidence: string[];
};

export type ResourcePresentation = {
  actions: ResourceAction[];
  priceLabel: string;
  equipmentLabel: string | null;
  price: string;
};

const priceLabels: Record<ResourceLine["price_status"], string> = {
  catalog_unknown: "Цена не проверена",
  ambiguous: "Соответствие неоднозначно",
  specification_missing: "Недостаточно характеристик",
  price_absent: "Цена в выбранной базе отсутствует",
  found: "Цена найдена",
};

const equipmentLabels: Record<ResourceLine["equipment_status"], string> = {
  unchecked: "Комплектность не проверена",
  documented: "Состав раскрыт в проекте",
  series_confirmed: "Точная серия подтверждена",
  designer: "Требует уточнения проектировщиком",
};

const isMissing = (value: string | null | undefined): boolean => value == null || value.trim() === "";
const evidenceLines = (...values: (string | undefined)[]): string[] =>
  [...new Set(values.filter((value): value is string => typeof value === "string" && value.trim() !== ""))];

/** Display existing resource facts without interpreting free-form notes as current state. */
export function resourcePresentation(line: ResourceLine): ResourcePresentation {
  const actions: ResourceAction[] = [];
  if (isMissing(line.quantity)) actions.push({
    key: "quantity", topic: "Количество", title: "Количество не установлено",
    action: "Уточнить количество по источникам", tone: "missing",
    evidence: ["Поле количества ресурса не заполнено. Количество нужно установить по исходным документам."],
  });
  if (isMissing(line.unit)) actions.push({
    key: "unit", topic: "Единица", title: "Единица не указана",
    action: "Уточнить единицу измерения", tone: "missing",
    evidence: ["Поле единицы измерения ресурса не заполнено."],
  });

  const missingPrice = isMissing(line.unit_price);
  let priceLabel = priceLabels[line.price_status] || line.status_label || "Состояние цены не указано";
  // Keep the raw reason even when the register formatter suppresses a copy already in note.
  const priceEvidence = evidenceLines(line.price_reason, line.search_evidence);
  const priceAction = (title: string, action: string, tone: ResourceAction["tone"]) => actions.push({
    key: "price", topic: "Цена", title, action, tone, evidence: priceEvidence,
  });
  switch (line.price_status) {
    case "catalog_unknown":
      priceAction(priceLabel, "Проверить базу и цену", "review");
      break;
    case "ambiguous":
      priceAction(priceLabel, "Уточнить исполнение ресурса", "review");
      break;
    case "specification_missing":
      priceAction(priceLabel, "Уточнить характеристики для поиска цены", "missing");
      break;
    case "price_absent":
      priceAction(priceLabel, "Подготовить КАЦ", "price");
      break;
    case "found":
      if (missingPrice) {
        priceLabel = "Цена отмечена найденной; значение не указано";
        priceAction(priceLabel, "Проверить данные цены", "missing");
      }
      break;
    default:
      priceAction(priceLabel, "Проверить состояние цены", "review");
  }

  // unchecked is a schema default for every resource, not proof of missing equipment.
  const equipmentEvidence = evidenceLines(line.equipment_evidence, line.manufacturer_url);
  const equipmentRelevant = line.equipment_status !== "unchecked" || equipmentEvidence.length > 0;
  const equipmentLabel = equipmentRelevant ? equipmentLabels[line.equipment_status] || null : null;
  if (line.equipment_status === "designer") actions.push({
    key: "equipment", topic: "Комплектность", title: "Комплектность требует уточнения",
    action: "Уточнить у проектировщика", tone: "review", evidence: equipmentEvidence,
  });
  else if (line.equipment_status === "unchecked" && equipmentRelevant) actions.push({
    key: "equipment", topic: "Комплектность", title: "Комплектность не проверена",
    action: "Проверить сведения о комплектности", tone: "review", evidence: equipmentEvidence,
  });

  return {
    actions, priceLabel, equipmentLabel,
    price: missingPrice ? "" : registerPrice(line).price,
  };
}

/** The API already groups a shared source fact; do not aggregate quantities or infer identities. */
export function resourceWorkIds(line: ResourceLine): string[] {
  return [...new Set([line.work_id, ...(line.work_ids || [])]
    .filter(id => typeof id === "string" && id.trim() !== ""))];
}
